import React, { Fragment } from 'react'
import TextSnippetTwoToneIcon from '@mui/icons-material/TextSnippetTwoTone'
import { Button, Modal, SvgIcon } from '../../../components'
import { useModal } from '../../../utils/hooks'
import { RenderIf } from '../../../utils/fns'
import { APP_CONFIG } from '../common/constants'

const AboutModal = () => {
  const [show, onHide] = useModal(false)

  return (
    <Fragment>
      <Button variant="topNav" className="button topNav__button" onClick={onHide}>
        <TextSnippetTwoToneIcon className="material-icons-two-tone material-icons-two-tone--noinvert" />
        <span className="button__text">About</span>
      </Button>
      {RenderIf(
        show,
        <Modal
          show={show}
          onHide={onHide}
          className="-about"
          header={
            <Modal.Header closeButton>
              <div className="appBrandingGroup">
                <SvgIcon
                  src={APP_CONFIG.logoUrl}
                  svgClasses="iconic-sm appLogo ml-0 w-100"
                  alt="PFD Logo"
                />
                <div className="aboutAppHeading">
                  <div className="aboutAppAcronym">
                    <span className="aboutAppAcronym__label"></span>
                    {APP_CONFIG.appCode}
                  </div>
                  <div className="aboutAppFullName">{APP_CONFIG.appName}</div>
                </div>
              </div>
            </Modal.Header>
          }
        >
          <Fragment>
            <p className="intro">
              PFD streamlines the design of anchorage and holdown solutions for specific uplift
              loads, adhering to current building codes. It effectively eliminates the need for
              multiple desktop applications and tracks several variables.
            </p>

            <div className="key-features">
              <p className="intro">Key Features:</p>
              <ul>
                <li>
                  <span>Dynamic UI:</span> Real-time display of Simpson Strong-Tie product solutions
                  as you adjust inputs.
                </li>
                <li>
                  <span>Design Flexibility:</span> Create solutions for holdowns embedded in
                  concrete or added post-pour. Tailor your designs with various material types,
                  geometric constraints, and post bases for structural support.
                </li>
                <li>
                  <span>Anchorage Options:</span> Choose from cast-in-place, post-installed
                  adhesive, and mechanical anchors.
                </li>
                <li>
                  <span>Foundation Selection:</span> Choose from standard foundation types, specify
                  dimensions, and ensure your designs are anchored securely with a suitable post
                  base.
                </li>
                <li>
                  <span>Detailed Outputs:</span> Generate a detailed, print-ready summary complete
                  with all calculation assumptions. You can also save and reuse designs for future
                  projects.
                </li>
              </ul>
            </div>

            <h4>
              <span className="release-number">{APP_CONFIG.appVersion}</span>
              <span className="release-date">Released on September 4, 2025</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Addition"
                  />
                  Addition
                </div>
                <div className="release-info">
                  Added new products for USA: SABR™ Anchor Bolt, RFB Retrofit Bolt, PBST Post Base,
                  Sage-B Post Base and MPBZ™ Moment Post Base
                </div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/gear.svg" alt="Change Icon" />
                  Change
                </div>
                <div className="release-info">Maintenance</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">2.2.0</span>
              <span className="release-date">Released on April 3, 2025</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Addition"
                  />
                  Addition
                </div>
                <div className="release-info">
                  Added new products for USA: HDUE (holdown), CBST-Z and CBSTQ-Z (post base)
                </div>
              </li>

              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Addition"
                  />
                  Addition
                </div>
                <div className="release-info">
                  Added new products for Canada based on the C-C-CAN2025 catalog: ABA24-2Z, ECB66,
                  ECB66HDG, ECB88, ECB88HDG (post base) and HTTH6.5 (holdown)
                </div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/gear.svg" alt="Change Icon" />
                  Change
                </div>
                <div className="release-info">Maintenance</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">2.1.1</span>
              <span className="release-date">Released on November 14, 2024</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/gear.svg" alt="Change Icon" />
                  Change
                </div>
                <div className="release-info">Maintenance</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">2.1.0</span>
              <span className="release-date">Released on August 1, 2024</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Addition"
                  />
                  Addition
                </div>
                <div className="release-info">
                  Added option to design for Post Bases and Holdowns for Canada
                </div>
              </li>
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Addition"
                  />
                  Addition
                </div>
                <div className="release-info">
                  Added HTTH6.5 & HTTH6.5HDG - New Heavy Tension Tie Designed for Higher Loads (USA)
                </div>
              </li>
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Addition"
                  />
                  Addition
                </div>
                <div className="release-info">
                  Added APB/APVB - Decorative Post Bases installed atop single 2x wood members (USA)
                </div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">2.0.0</span>
              <span className="release-date">Released on February 22, 2024</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Change"
                  />
                  Addition
                </div>
                <div className="release-info">Added option to design for Post Bases</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">1.5.0</span>
              <span className="release-date">Released on November 9, 2023</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" alt="Bug Fix Icon" />
                  Bug Fix
                </div>
                <div className="release-info">Minor bug fixes</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">1.4.0</span>
              <span className="release-date">Released on September 28, 2023</span>
            </h4>
            <ul className="release-note">
              <li>
                <div className="release-type release-addition">
                  <SvgIcon
                    src="assets/images/iconic/fork.svg"
                    svgClasses="iconic-sm"
                    alt="Change"
                  />
                  Addition
                </div>
                <div className="release-info">Added ET-3G™ Epoxy Adhesive</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">1.3.0</span>
              <span className="release-date">Released on July 27, 2023</span>
            </h4>
            <ul className="release-note">
              <li>
                <div className="release-type release-addition">
                  <SvgIcon src="assets/images/iconic/fork.svg" alt="Change" />
                  Addition
                </div>
                <div className="release-info">
                  Added AT-3G™ High-Strength Hybrid Acrylic Adhesive
                </div>
              </li>
            </ul>

            <h4>
              <span className="release-number">1.2.0</span>
              <span className="release-date">Released on April 8, 2022</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" />
                  Bug Fix
                </div>
                <div className="release-info">Variety of bug fixes</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" />
                  Bug Fix
                </div>
                <div className="release-info">Changes to footnotes</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" />
                  Bug Fix
                </div>
                <div className="release-info">Stability of the application</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">1.1.0</span>
              <span className="release-date">Released on February 12, 2021</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" />
                  Change
                </div>
                <div className="release-info">Major data enhancements</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" />
                  Change
                </div>
                <div className="release-info">Out of beta</div>
              </li>
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" />
                  Change
                </div>
                <div className="release-info">UI/UX upgrades</div>
              </li>
              <li>
                <div className="release-type release-addition">
                  <SvgIcon src="assets/images/iconic/fork.svg" />
                  Addition
                </div>
                <div className="release-info">Added new applications</div>
              </li>
              <li>
                <div className="release-type release-addition">
                  <SvgIcon src="assets/images/iconic/fork.svg" />
                  Addition
                </div>
                <div className="release-info">Expanded design criteria</div>
              </li>
              <li>
                <div className="release-type release-newFeature">
                  <SvgIcon src="assets/images/iconic/star-empty.svg" />
                  New Feature
                </div>
                <div className="release-info">Added interactive tutorial</div>
              </li>
              <li>
                <div className="release-type release-newFeature">
                  <SvgIcon src="assets/images/iconic/star-empty.svg" />
                  New Feature
                </div>
                <div className="release-info">Ability to export solutions</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" />
                  Bug Fix
                </div>
                <div className="release-info">Variety of bug fixes</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" />
                  Bug Fix
                </div>
                <div className="release-info">Changes to footnotes</div>
              </li>
              <li>
                <div className="release-type release-bugFix">
                  <SvgIcon src="assets/images/iconic/bug.svg" />
                  Bug Fix
                </div>
                <div className="release-info">Stability of the application</div>
              </li>
            </ul>

            {/*
"Change" Major Data enhancements, Added an Interactive Tutorial, Out of Beta, UI/UX Upgrades, Ability to Export Solutions.
"Updates" Added New Applications and Expanded Design Criteria,
"Bug Fixes" Variety of Bugs Fixes, Changes to footnotes, Stability of the Application,
*/}

            <h4>
              <span className="release-number">1.0.0 beta 2</span>
              <span className="release-date">Released on July 14, 2020</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-change">
                  <SvgIcon src="assets/images/iconic/pencil.svg" />
                  Change
                </div>
                <div className="release-info">Minor fixes for analytics.</div>
              </li>
            </ul>

            <h4>
              <span className="release-number">1.0.0 beta 1</span>
              <span className="release-date">Released on June 23, 2020</span>
            </h4>

            <ul className="release-note">
              <li>
                <div className="release-type release-initial">
                  <SvgIcon src="assets/images/iconic/magic-wand.svg" alt="Initial" />
                  Initial
                </div>
                <div className="release-info">Initial beta release of the application.</div>
              </li>
            </ul>

            {/*
<h4><span className="release-number">0.0.1</span><span className="release-date">Released on July 24, 2019</span></h4>

<ul className="release-note">
  <li>
    <div className="release-type release-initial"><img data-src="assets/images/iconic/magic-wand.svg" className="iconic iconic-sm injectSVG" alt="Initial" />Initial</div>
    <div className="release-info">Renamed the application in recognition of current and future feature sets that do more than screw substitution.</div>
  </li>
</ul>
*/}
          </Fragment>
        </Modal>,
      )}
    </Fragment>
  )
}

export default AboutModal
